/**
 * Quality-signal ranking (#626 Part 3) — the gap #262 flagged and neither signal
 * epic closed.
 *
 * #262's problem statement is that "every catalog entry presents identically
 * whether it is used daily across the enterprise or has never been installed …
 * discovery favours whichever entry was pushed most recently rather than whichever
 * is most useful". #262 answered that for BROWSE (a Most Used sort) and #506 added
 * votes on top (a Recommended sort). Neither reached SEARCH: `filterPlugins` skips
 * `sortPlugins` entirely while a query is active, so the moment a user types
 * anything, every quality signal is discarded and #262's complaint is true again.
 *
 * This module is the signal half. `search.ts` blends it with relevance.
 *
 * The formula is #506 §5.3, implemented literally
 * ───────────────────────────────────────────────
 *   Score = dominant-weighted log10(activeUsers30d + 1)  [usage]
 *         + useful-vote ratio                            [usefulness]
 *
 *   - When activeUsers30d is ABSENT the usage term is dropped entirely. Absence
 *     means "unmeasured", never a measured zero — the upstream mart holds no row
 *     for an unused skill (Contract 7), so scoring it as 0 users would punish an
 *     entry for a data gap. Measured coverage is ~41%, so this is the MAJORITY
 *     path, not an edge case.
 *   - Below MIN_VOTES total votes the usefulness term is a neutral 0.5 rather than
 *     the raw ratio, so one enthusiastic vote cannot read as "100% useful" and a
 *     lightly-voted entry is not penalised against an established one.
 *   - Downloads / stickiness are excluded (no reliable source exists).
 *   - Official CAI is excluded: it is a curation and support signal, not evidence
 *     that a skill works in practice. #506 is explicit about this.
 */

import type { MarketplaceEntry } from "../types/index";
import { MIN_VOTES_FOR_RATIO } from "@skills-marketplace/contracts/contract-8-feedback";

/**
 * #506 §5.3: below this many total votes, usefulness is neutral rather than raw.
 *
 * Bound to Contract 8's MIN_VOTES_FOR_RATIO, NOT a local `5`: the card/badge gate
 * (usefulnessDisplay) and this ranking gate must move together. The prototype hardcoded
 * it in both places, which is exactly how a display gate and a ranking gate drift apart.
 */
export const MIN_VOTES = MIN_VOTES_FOR_RATIO;

/** The neutral usefulness value used below MIN_VOTES. */
export const NEUTRAL_USEFULNESS = 0.5;

/**
 * Weight on the usage term. #506 §5.3 says "dominant-weighted" without defining a
 * number, so this is deliberately 1 rather than an invented constant: log10(n + 1)
 * spans roughly 0.3–2.6 across realistic user counts while the usefulness ratio is
 * bounded 0–1, so at weight 1 usage already dominates arithmetically. Raising this
 * is a product decision for @Bryce-Evans, not a tuning knob to guess at.
 */
export const USAGE_WEIGHT = 1;

export interface SignalBreakdown {
  /** #506 §5.3 score. Unbounded above; normalise before combining with relevance. */
  score: number;
  /** The usage term, or null when the entry is unmeasured (absent, not zero). */
  usage: number | null;
  /** The usefulness term actually used — the raw ratio, or NEUTRAL_USEFULNESS. */
  usefulness: number;
  /** True when usefulness fell back to neutral because votes < MIN_VOTES. */
  usefulnessIsNeutral: boolean;
}

/**
 * #506 §5.3, per entry.
 *
 * Returns a breakdown rather than a bare number so a UI can explain a ranking
 * ("40 users, 8 of 9 found useful") and so a test can assert WHICH branch ran
 * instead of only the total — the unmeasured and neutral paths are the two most
 * likely to regress silently.
 */
export function signalScore(entry: MarketplaceEntry): SignalBreakdown {
  const activeUsers = entry.adoption?.activeUsers30d;
  // Explicitly test for a number: `undefined` is unmeasured, and a defensive `0`
  // from a malformed artifact must not be treated as a real measurement either
  // (Contract 7 guarantees >= 1 when present).
  const usage = typeof activeUsers === "number" && activeUsers > 0
    ? USAGE_WEIGHT * Math.log10(activeUsers + 1)
    : null;

  const tally = entry.feedback;
  const hasEnoughVotes = !!tally && tally.total >= MIN_VOTES;
  const usefulness = hasEnoughVotes
    ? tally.useful / tally.total
    : NEUTRAL_USEFULNESS;

  return {
    score: (usage ?? 0) + usefulness,
    usage,
    usefulness,
    usefulnessIsNeutral: !hasEnoughVotes,
  };
}

/**
 * Scale a set of raw signal scores into 0–1 for blending.
 *
 * Normalised across the CURRENT result set rather than the whole catalog, to match
 * how relevance is normalised in search.ts — both terms must share a scale or the
 * blend silently weights whichever happens to have the larger range.
 *
 * A degenerate set (all scores equal, including a single result) yields 0 for
 * every entry rather than 1: with nothing to distinguish, the signal should not
 * shift the ordering at all, and returning 1 across the board would multiply every
 * result by the same constant while implying the signal had something to say.
 */
export function normaliseSignals(scores: number[]): number[] {
  if (scores.length === 0) return [];
  const min = Math.min(...scores);
  const max = Math.max(...scores);
  const span = max - min;
  if (span === 0) return scores.map(() => 0);
  return scores.map((s) => (s - min) / span);
}
